import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { GlowBlob } from "../../../ui/waq/GlowBlob";
import { talentSplash as copy } from "../../../../content/waq/talentSplash";
import { readStoredConsent } from "@/utils/analytics";
import { qaTalentEvents } from "@/utils/qaTalentAnalytics";
import {
  CONSENT_SAVED_EVENT,
  SITE_POPUP_ATTRIBUTE,
  hasSeenSplash,
  markSplashSeen,
  splashBlockedBy,
} from "@/utils/talentSplash";

/** About three seconds in: long enough to see where you are first. */
const DELAY_MS = 3000;
/** After the cookie banner is answered, a shorter beat so the two don't feel chained. */
const AFTER_CONSENT_MS = 1500;

/**
 * The home page's QA Talent offer, once per visit.
 *
 * Client-only by construction: it starts closed and opens from a timer, so the
 * prerendered HTML and crawlers never see it, and it costs the LCP nothing.
 * On phones it is a card at the bottom rather than a full-screen takeover.
 */
export function TalentSplash() {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const facts = () => ({
      hash: window.location.hash,
      consentAnswered: readStoredConsent() !== null,
      seenThisVisit: hasSeenSplash(),
      otherPopupOpen: document.querySelector(`[${SITE_POPUP_ATTRIBUTE}]`) !== null,
    });

    const openIfAllowed = () => {
      if (splashBlockedBy(facts())) return;
      markSplashSeen();
      setOpen(true);
      qaTalentEvents.splashView();
    };

    const schedule = (delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(openIfAllowed, delay);
    };

    const block = splashBlockedBy(facts());
    if (block === "deep-link" || block === "seen") return;

    const onConsent = () => schedule(AFTER_CONSENT_MS);
    if (block === "consent-pending") window.addEventListener(CONSENT_SAVED_EVENT, onConsent);
    else schedule(DELAY_MS);

    return () => {
      clearTimeout(timer);
      window.removeEventListener(CONSENT_SAVED_EVENT, onConsent);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    qaTalentEvents.splashDismiss();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? setOpen(true) : dismiss())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-navy/55 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          ref={contentRef}
          tabIndex={-1}
          // Focus the dialog, not its first control: on a timed open the close
          // button drew a focus ring nobody asked for, and a screen reader now
          // starts from the title and text rather than from "Close".
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            contentRef.current?.focus();
          }}
          aria-describedby="talent-splash-body"
          className="fixed inset-x-3 bottom-3 z-[61] overflow-hidden rounded-panel border border-ondark-border bg-navy p-7 text-surface shadow-hero-panel outline-none [box-shadow:inset_0_1px_0_rgba(255,255,255,0.08),0_30px_70px_-30px_rgba(0,0,0,0.6)] data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-6 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[min(600px,calc(100vw-48px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:p-10 sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-bottom-0"
        >
          {/* Navy on a navy-dimmed page: the on-dark card border and an inner top
              highlight are what keep the panel's edges from dissolving into it. */}
          <GlowBlob position={{ top: -260, right: -220 }} size={620} alpha={0.4} falloff="62%" />

          <div className="relative">
            <Dialog.Close
              aria-label="Close"
              className="absolute -right-2 -top-2 rounded-full p-2 text-surface/70 transition-colors hover:bg-white/10 hover:text-surface sm:-right-4 sm:-top-4"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </Dialog.Close>

            <p className="mb-3 text-eyebrow font-extrabold uppercase text-yellow">{copy.eyebrow}</p>
            <Dialog.Title className="mb-4 max-w-[480px] text-h3 font-extrabold">{copy.heading}</Dialog.Title>
            <p id="talent-splash-body" className="mb-6 text-body-lg text-ondark-body">
              {copy.body}
            </p>

            <p className="mb-7 flex items-baseline gap-3 border-l-[6px] border-yellow pl-4">
              <span className="text-body text-ondark-body">{copy.price.label}</span>
              <span className="text-stat-sm font-extrabold tabular-nums">{copy.price.value}</span>
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={copy.cta.href}
                onClick={() => {
                  qaTalentEvents.splashCtaClick();
                  setOpen(false);
                }}
                className="rounded-btn bg-yellow px-[30px] py-[17px] text-center text-btn font-bold text-navy hover:text-navy"
              >
                {copy.cta.label}
              </Link>
              <Dialog.Close className="rounded-btn border-[1.5px] border-white/30 px-[26px] py-[16px] text-btn font-semibold text-surface">
                {copy.dismiss}
              </Dialog.Close>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
